import { useState, useMemo, useRef, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronRight, MoreVertical, UtensilsCrossed, Home, Pencil, Trash2, Plus, Heart, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import type { RSVP, Guest } from "./GuestSummaryCards";

interface GuestListProps {
  rsvps: RSVP[];
  onEditRSVP?: (rsvp: RSVP) => void;
  onDeleteRSVP?: (rsvp: RSVP) => void;
  onEditGuest?: (rsvp: RSVP, guest: Guest) => void;
  onDeleteGuest?: (rsvp: RSVP, guest: Guest) => void;
  onAddGuest?: (rsvp: RSVP) => void;
  searchQuery?: string;
  filter?: "all" | "confirmed" | "pending" | "declined";
  viewMode?: "rsvp" | "guests";
  expandRSVPId?: string; // RSVP ID to automatically expand
  /** JUB-51: extra per-RSVP content shown in the expanded row (short links). */
  renderRsvpExtras?: (rsvp: RSVP) => ReactNode;
}

export function GuestList({
  rsvps,
  onEditRSVP,
  onDeleteRSVP,
  onEditGuest,
  onDeleteGuest,
  onAddGuest,
  searchQuery = "",
  filter = "all",
  viewMode = "rsvp",
  expandRSVPId,
  renderRsvpExtras,
}: GuestListProps) {
  const { t } = useTranslation();
  const [expandedRSVPs, setExpandedRSVPs] = useState<Set<string>>(new Set());
  
  // Auto-expand RSVP when expandRSVPId is provided
  useEffect(() => {
    if (expandRSVPId && !expandedRSVPs.has(expandRSVPId)) {
      setExpandedRSVPs((prev) => new Set([...prev, expandRSVPId]));
    }
  }, [expandRSVPId, expandedRSVPs]);
  

  const toggleRSVP = (rsvpId: string) => {
    setExpandedRSVPs((prev) => {
      const next = new Set(prev);
      if (next.has(rsvpId)) {
        next.delete(rsvpId);
      } else {
        next.add(rsvpId);
      }
      return next;
    });
  };

  const filteredRSVPs = useMemo(() => {
    
    // Ensure rsvps is an array and each RSVP has a guests array and required fields
    const safeRsvps = (Array.isArray(rsvps) ? rsvps : []).map((rsvp, index) => {
      
      // Ensure id exists - use guest_uuid or id or generate one
      const rsvpId = rsvp.id || (rsvp as unknown as Record<string, string>).guest_uuid || (rsvp as unknown as Record<string, string>).uuid || `rsvp-${index}`;

      return {
        id: rsvpId,
        eventId: rsvp.eventId || (rsvp as unknown as Record<string, string>).event_id || '',
        email: rsvp.email || '',
        phone: rsvp.phone,
        familyName: rsvp.familyName || (rsvp as unknown as Record<string, string>).family_name,
        tableName: rsvp.tableName ?? (rsvp as unknown as Record<string, string>).table_name ?? undefined,
        status: rsvp.status || 'pending',
        submittedAt: rsvp.submittedAt || (rsvp as unknown as Record<string, string>).submitted_at,
        notes: rsvp.notes ?? undefined,
        guests: Array.isArray(rsvp.guests) ? rsvp.guests : [],
      };
    });
    
    
    let filtered = safeRsvps;

    // Apply filter
    if (filter !== "all") {
      filtered = filtered.filter((rsvp) => rsvp.status === filter);
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (rsvp) =>
          rsvp.familyName?.toLowerCase().includes(query) ||
          rsvp.tableName?.toLowerCase().includes(query) ||
          rsvp.email?.toLowerCase().includes(query) ||
          rsvp.phone?.toLowerCase().includes(query) ||
          rsvp.notes?.toLowerCase().includes(query) ||
          (Array.isArray(rsvp.guests) && rsvp.guests.some(
            (guest) =>
              guest.name?.toLowerCase().includes(query) ||
              guest.menuPreference?.toLowerCase().includes(query)
          ))
      );
    }

    return filtered;
  }, [rsvps, filter, searchQuery]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "—";
    }
  };

  const getStatusBadgeVariant = (status: RSVP["status"]) => {
    switch (status) {
      case "confirmed":
        return "default";
      case "pending":
        return "secondary";
      case "declined":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getMenuIcon = (preference?: string) => {
    if (!preference) return null;
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <UtensilsCrossed className="h-3 w-3" />
        <span className="capitalize">{preference === "vegetarian" ? "Vegg" : preference === "vegan" ? "Vgn" : "Std"}</span>
      </div>
    );
  };

  const getCeremonyLabel = (attendance?: { civil?: boolean; religious?: boolean; baptism?: boolean; party?: boolean }) => {
    if (!attendance) return null;
    const parts: string[] = [];
    if (attendance.civil) parts.push(t("events.subEvents.civilCeremony"));
    if (attendance.religious) parts.push(t("events.subEvents.religiousCeremony"));
    if (attendance.baptism) parts.push(t("events.subEvents.baptismCeremony"));
    if (attendance.party) parts.push(t("events.subEvents.reception"));
    if (parts.length === 0) return null;
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Heart className="h-3 w-3" />
        <span>{parts.join(", ")}</span>
      </div>
    );
  };

  if (viewMode === "guests") {
    // Flat list of all guests
    const allGuests = filteredRSVPs.flatMap((rsvp) =>
      (Array.isArray(rsvp.guests) ? rsvp.guests : []).map((guest) => ({ rsvp, guest }))
    );

    return (
      <div className="space-y-2">
        {allGuests.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            {t("events.form.noGuestsFound") || t("guests.noGuests")}
          </p>
        ) : (
          allGuests.map(({ rsvp, guest }) => (
            <div
              key={`${rsvp.id}-${guest.id}`}
              className="flex items-center justify-between rounded-lg border bg-card p-4"
            >
              <div className="flex items-center gap-3 flex-1">
                <Badge variant={guest.type === "adult" ? "default" : "secondary"}>
                  {guest.type === "adult"
                    ? t("guests.adult")
                    : t("guests.kid")}
                </Badge>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-foreground">{guest.name}</p>
                    {/* JUB-85: per-row RSVP status so the flat view works as a venue list */}
                    <Badge variant={getStatusBadgeVariant(rsvp.status)}>
                      {t(`events.form.status.${rsvp.status}`) || rsvp.status}
                    </Badge>
                    {(guest.tableName || rsvp.tableName) && (
                      <Badge variant="outline" data-testid="guest-table-badge">
                        {guest.tableName || rsvp.tableName}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {getMenuIcon(guest.menuPreference)}
                    {guest.accommodationNeeded && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Home className="h-3 w-3" />
                        <span>{t("events.form.accommodation") || t("rsvp.accommodation")}</span>
                      </div>
                    )}
                    {getCeremonyLabel(guest.ceremonyAttendance)}
                  </div>
                  {rsvp.notes && <RsvpNote notes={rsvp.notes} />}
                </div>
              </div>
              <GuestActionsMenu
                onEdit={onEditGuest ? () => onEditGuest(rsvp, guest) : undefined}
                onDelete={onDeleteGuest ? () => onDeleteGuest(rsvp, guest) : undefined}
              />
            </div>
          ))
        )}
      </div>
    );
  }

  // RSVP view with expandable groups
  return (
    <div className="space-y-2">
      {filteredRSVPs.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">
          {searchQuery.trim() ? t("events.form.noRSVPsFound") : t("events.form.noRSVPsYet")}
        </p>
      ) : (
        filteredRSVPs.map((rsvp) => {
          const isExpanded = expandedRSVPs.has(rsvp.id);
          return (
            <div key={rsvp.id} data-testid="rsvp-row" className="rounded-lg border bg-card overflow-hidden">
              {/* RSVP Header */}
              <div
                data-testid="rsvp-row-header"
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleRSVP(rsvp.id)}
              >
                <div className="flex items-center gap-3 flex-1">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground">
                        {rsvp.familyName || rsvp.email}
                      </p>
                      <Badge variant={getStatusBadgeVariant(rsvp.status)}>
                        {t(`events.form.status.${rsvp.status}`) || rsvp.status}
                      </Badge>
                      {rsvp.tableName && (
                        <Badge variant="outline" data-testid="rsvp-table-badge">
                          {rsvp.tableName}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      {/* JUB-85: email may be empty — join only the parts that exist */}
                      <span>
                        {[
                          rsvp.email,
                          rsvp.phone,
                          (() => {
                            const n = Array.isArray(rsvp.guests) ? rsvp.guests.length : 0;
                            return `${n} ${n === 1 ? t("events.form.guestSingular") : (t("events.form.guests") || t("guests.title"))}`;
                          })(),
                        ]
                          .filter(Boolean)
                          .join(" • ")}
                      </span>
                    </div>
                    {rsvp.notes && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <RsvpNote notes={rsvp.notes} />
                      </div>
                    )}
                  </div>
                  <div
                    className="flex items-center gap-2 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <span className="text-xs text-muted-foreground">
                      {formatDate(rsvp.submittedAt)}
                    </span>
                    <RSVPActionsMenu
                      onEdit={onEditRSVP ? () => onEditRSVP(rsvp) : undefined}
                      onDelete={onDeleteRSVP ? () => onDeleteRSVP(rsvp) : undefined}
                    />
                  </div>
                </div>
              </div>

              {/* Expanded Guest List */}
              {isExpanded && (
                <div data-testid="rsvp-guests-expanded" className="border-t bg-muted/30">
                  {(Array.isArray(rsvp.guests) ? rsvp.guests : []).map((guest) => (
                    <div
                      key={guest.id}
                      data-testid="guest-row"
                      className="flex items-center justify-between p-4 pl-12 border-b last:border-0"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Badge variant={guest.type === "adult" ? "default" : "secondary"}>
                          {guest.type === "adult"
                            ? t("guests.adult")
                            : t("guests.kid")}
                        </Badge>
                        <div>
                          <p className="font-medium text-foreground">{guest.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {getMenuIcon(guest.menuPreference)}
                            {guest.accommodationNeeded && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Home className="h-3 w-3" />
                                <span>{t("events.form.accommodation") || t("rsvp.accommodation")}</span>
                              </div>
                            )}
                            {getCeremonyLabel(guest.ceremonyAttendance)}
                          </div>
                        </div>
                      </div>
                      <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        <GuestActionsMenu
                          onEdit={onEditGuest ? () => onEditGuest(rsvp, guest) : undefined}
                          onDelete={onDeleteGuest ? () => onDeleteGuest(rsvp, guest) : undefined}
                        />
                      </div>
                    </div>
                  ))}
                  {renderRsvpExtras?.(rsvp)}
                  {onAddGuest && (
                    <div className="p-4 pl-12">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onAddGuest(rsvp)}
                        className="w-full"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        {t("guests.addGuest") || t("guests.add")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// JUB-58: the guest's free-text message / dietary note. It was stored by the
// public form but never shown to the organizer. Long notes are clamped with an
// explicit expand toggle so a paragraph does not push the row layout around.
const NOTE_CLAMP_LENGTH = 140;

function RsvpNote({ notes }: { notes: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isLong = notes.length > NOTE_CLAMP_LENGTH;
  const shown = !isLong || expanded ? notes : `${notes.slice(0, NOTE_CLAMP_LENGTH).trimEnd()}…`;

  return (
    <div
      data-testid="rsvp-notes"
      className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-50 dark:bg-amber-950/30 px-2 py-1.5 text-xs text-foreground"
    >
      <MessageSquare className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden="true" />
      <div className="min-w-0">
        <span className="font-medium text-amber-800 dark:text-amber-300">{t("guests.rsvpNotesLabel")}: </span>
        <span className="whitespace-pre-line break-words">{shown}</span>
        {isLong && (
          <button
            type="button"
            className="ml-1 underline text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          >
            {expanded ? t("common.showLess") : t("common.showMore")}
          </button>
        )}
      </div>
    </div>
  );
}

function RSVPActionsMenu({
  onEdit,
  onDelete,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target) && buttonRef.current && !buttonRef.current.contains(target)) {
        setIsOpen(false);
        setMenuPosition(null);
      }
    };

    if (isOpen) {
      const calculatePosition = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const menuW = 192;
          const menuH = 100;
          let left = rect.right - menuW;
          let top = rect.bottom + 8;
          left = Math.max(8, Math.min(left, window.innerWidth - menuW - 8));
          top = Math.max(8, Math.min(top, window.innerHeight - menuH - 8));
          setMenuPosition({ top, left });
        }
      };
      calculatePosition();
      const timeoutId = setTimeout(calculatePosition, 10);
      const addListenerId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        clearTimeout(addListenerId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    } else {
      setMenuPosition(null);
    }
  }, [isOpen]);

  const menuContent = isOpen && menuPosition ? (
    <div
      ref={menuRef}
      className="fixed z-[9999] w-48 rounded-md border border-border bg-background shadow-lg"
      style={{
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
      }}
    >
      <div className="p-1">
        {onEdit && (
          <button
            type="button"
            data-testid="rsvp-action-edit"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
              setIsOpen(false);
            }}
          >
            <Pencil className="h-4 w-4" />
            {t("common.edit")}
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            data-testid="rsvp-action-delete"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-md transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
              setIsOpen(false);
            }}
          >
            <Trash2 className="h-4 w-4" />
            {t("common.delete")}
          </button>
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="relative" onMouseDown={(e) => e.stopPropagation()}>
        <Button
          ref={buttonRef}
          variant="ghost"
          size="icon"
          type="button"
          data-testid="rsvp-actions-trigger"
          aria-label={t("guests.rsvpActions")}
          className="h-8 w-8"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </div>
      {menuContent && createPortal(menuContent, document.body)}
    </>
  );
}

function GuestActionsMenu({
  onEdit,
  onDelete,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target) && buttonRef.current && !buttonRef.current.contains(target)) {
        setIsOpen(false);
        setMenuPosition(null);
      }
    };

    if (isOpen) {
      const calculatePosition = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const menuW = 192;
          const menuH = 100;
          let left = rect.right - menuW;
          let top = rect.bottom + 8;
          left = Math.max(8, Math.min(left, window.innerWidth - menuW - 8));
          top = Math.max(8, Math.min(top, window.innerHeight - menuH - 8));
          setMenuPosition({ top, left });
        }
      };
      calculatePosition();
      const timeoutId = setTimeout(calculatePosition, 10);
      const addListenerId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        clearTimeout(addListenerId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    } else {
      setMenuPosition(null);
    }
  }, [isOpen]);

  const menuContent = isOpen && menuPosition ? (
    <div
      ref={menuRef}
      className="fixed z-[9999] w-48 rounded-md border border-border bg-background shadow-lg"
      style={{
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
      }}
    >
      <div className="p-1">
        {onEdit && (
          <button
            type="button"
            data-testid="guest-action-edit"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
              setIsOpen(false);
            }}
          >
            <Pencil className="h-4 w-4" />
            {t("common.edit")}
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            data-testid="guest-action-delete"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-md transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
              setIsOpen(false);
            }}
          >
            <Trash2 className="h-4 w-4" />
            {t("common.delete")}
          </button>
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="relative" onMouseDown={(e) => e.stopPropagation()}>
        <Button
          ref={buttonRef}
          variant="ghost"
          size="icon"
          type="button"
          data-testid="guest-actions-trigger"
          aria-label={t("guests.guestActions")}
          className="h-8 w-8"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </div>
      {menuContent && createPortal(menuContent, document.body)}
    </>
  );
}

